import React, { useState, useEffect, useRef } from 'react';
import styles from '../Lists.module.css';
import { ListCommand } from '../../../types/ListCommands';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

interface LinkedListViewProps {
  command: ListCommand | null;
  onComplete: () => void;
}

const LinkedListView: React.FC<LinkedListViewProps> = ({ command, onComplete }) => {
  const [list, setList] = useState<{ value: number }[]>(() => 
    Array.from({ length: 3 }, () => ({ value: Math.floor(Math.random() * 100) }))
  );  
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [pointerCoords, setPointerCoords] = useState<{ top: number; left: number } | null>(null);
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (command) executeCommand(command);
  }, [command]);

  const traverseToIndex = async (index: number) => {
    for (let i = 0; i <= index; i++) {
      const nodeEl = nodeRefs.current[i];
      if (nodeEl) {
        setPointerCoords({ top: nodeEl.offsetTop, left: nodeEl.offsetLeft });
      }
      if (i < index) await delay(500);
    }
  };

  const executeCommand = async (cmd: ListCommand) => {
    if (list.length >= 8 && (cmd.action === 'addFirst' || cmd.action === 'addLast' || cmd.action === 'insertAt')) {
    } else {
        switch (cmd.action) {
            case 'addFirst':
                setList(prev => [{ value: cmd.value }, ...prev]);
                setHighlightedIndex(0);
                await delay(500);
                break;
            case 'addLast':
                if (list.length > 0) await traverseToIndex(list.length - 1);
                setList(prev => [...prev, { value: cmd.value }]);
                setHighlightedIndex(list.length);
                await delay(500);
                break;
            case 'removeFirst':
                if (list.length === 0) break;
                setHighlightedIndex(0);
                await delay(500);
                setList(prev => prev.slice(1));
                break;
            case 'removeLast':
                if (list.length === 0) break;
                await traverseToIndex(list.length - 1);
                setHighlightedIndex(list.length - 1);
                await delay(500);
                setList(prev => prev.slice(0, -1));
                break;
            case 'insertAt':
                if (cmd.index > 0) await traverseToIndex(cmd.index - 1);
                setList(prev => [...prev.slice(0, cmd.index), { value: cmd.value }, ...prev.slice(cmd.index)]);
                setHighlightedIndex(cmd.index);
                await delay(500);
                break;
            case 'removeAt':
                if (list.length === 0 || cmd.index >= list.length) break;
                await traverseToIndex(cmd.index);
                setHighlightedIndex(cmd.index);
                await delay(500);
                setList(prev => prev.filter((_, i) => i !== cmd.index));
                break;
            case 'reset':
                const newRandomState = Array.from({ length: 3 }, () => ({ value: Math.floor(Math.random() * 100) }));
                setList(newRandomState);
                await delay(300);
                break;   
        }
    }
    setHighlightedIndex(null);
    setPointerCoords(null);
    onComplete();
  };

  return (
    <div className={styles.comparisonContainer}>
      <h3 className={styles.comparisonTitle}>Linked List</h3>
      <div className={styles.visualization}>
        <div className={styles.linkedListContainer}>
          {list.map((node, i) => (
            <div key={i} className={styles.llNodeContainer} ref={el => { nodeRefs.current[i] = el; }}>
              <div className={`${styles.llNode} ${highlightedIndex === i ? styles.highlighted : ''}`}>
                {node.value}
              </div>
              {i < list.length - 1 && <div className={styles.llArrow}>&rarr;</div>}
            </div>
          ))}
          <div className={styles.llNull}>NULL</div>
          {pointerCoords && (
            <div className={styles.llPointer} style={{ top: `${pointerCoords.top - 40}px`, left: `${pointerCoords.left}px` }}>
              &#x21D3;
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkedListView;